import { useState, useEffect, useContext, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Avatar,
} from '@mui/material';
import { GridClearIcon } from '@mui/x-data-grid';
import { useAppDispatch } from 'store/hooks';
import { getChartSarchList } from 'store/slices/secureMessageSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { ROUTE } from 'route/RouteEnums';
import { validateSession } from 'services/validateSession';

interface PatientOption {
  id: number;
  label: string;
}

export default function SearchBox() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, setUserDetails } = useContext(AuthContext);

  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<PatientOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<PatientOption | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);

  // Normalize query for name/email/phone
  const normalizeQuery = (raw: string): string => {
    // convert non-breaking spaces, trim ends, collapse inner spaces
    const s = raw
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!s) return '';
    // phone: keep digits only if it looks like a phone
    const digits = s.replace(/\D+/g, '');
    if (digits.length >= 7) return digits; // e.g., "9876543210"
    // email: lowercase
    if (s.includes('@')) return s.toLowerCase();
    // name: already collapsed/trimmed
    return s;
  };

  // Search (debounced) – IMPORTANT: use normalized text for API
  useEffect(() => {
    const q = normalizeQuery ? normalizeQuery(inputValue) : inputValue.trim(); // if you already have normalizeQuery, use it
    if (q.length <= 2) {
      setOptions([]);
      setLoading(false);
      setOpen(false); // close when query too short
      return;
    }
    const t = setTimeout(() => {
      setLoading(true);
      dispatch(getChartSarchList(q))
        .unwrap()
        .then((response: any) => {
          if (response.status === 200) {
            const data = response?.data;
            const list = Object.entries(data).map(([id, label]) => ({
              id: Number(id),
              label: String(label),
            }));
            setOptions(list);
            setOpen(list.length > 0);
          }
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [inputValue, dispatch]);

  const rowSelect = async (patientId: number) => {
    await validateSession();    
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${patientId}`, '_blank');
    setUserDetails({ ...user, patientId });    
  };

  const clearAll = () => {
    setInputValue('');
    setSelectedOption(null);
    setOptions([]);
    setOpen(false); // close when cleared
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <Autocomplete<PatientOption, false, false, false>
      freeSolo={false}
      disableClearable={false} // allow value to be null
      options={options}
      value={selectedOption} // selected option persists
      inputValue={inputValue}
      loading={loading}
      fullWidth
      openOnFocus
      open={open}
      onOpen={() => {
        const q = normalizeQuery(inputValue);
        // only open if we have enough input — otherwise keep it closed
        if (q.length > 2) setOpen(true);
      }}
      onClose={(_, reason) => {
        // keep it open on outside click/blur
        if (reason === 'escape' || reason === 'toggleInput') {
          setOpen(false);
        } else {
          setOpen(true); // ignore 'blur' and 'selectOption' if you want it always open
        }
      }}
      disableCloseOnSelect // optional: keep open after selecting an option
      clearOnBlur={false}
      blurOnSelect={false}
      filterOptions={(x) => x} // show server results as-is
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'reset') return; // selection changed → MUI tries to "reset" input
        if (reason === 'clear') {
          // clear button / ESC
          setInputValue('');
          return;
        }
        setInputValue(newInput); // normal typing
      }} // keep raw typing; normalization happens in effect/paste
      onChange={(_, v, reason) => {
        if (v && reason === 'selectOption') {
          rowSelect(v.id); // your navigate + setUserDetails
          // clear the UI so the pipe-delimited label doesn't appear
          setSelectedOption(null);
          setInputValue('');
          setOptions([]);
          setOpen(false); // if you control the popup
          requestAnimationFrame(() => inputRef.current?.focus());
        }
      }}
      renderOption={(props, option) => {
        const [firstName, lastName, email, dob] = option.label.split('|');
        const name = `${firstName} ${lastName}`;
        const initials = name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase();
        return (
          <Box component='li' {...props} sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2', mr: 1.5 }}>{initials}</Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant='body1' fontWeight='bold'>
                {name}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                DOB: {dob}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {email}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label='Search...'
          variant='outlined'
          size='small'
          sx={{ width: 250 }}
          inputRef={inputRef}
          // Handle paste: normalize and keep focus/caret
          inputProps={{
            ...params.inputProps,
            onPaste: (e) => {
              const pasted = e.clipboardData.getData('text');
              e.preventDefault();
              const normalized = normalizeQuery(pasted);
              setInputValue(normalized);
              requestAnimationFrame(() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                  const len = inputRef.current.value.length;
                  inputRef.current.setSelectionRange(len, len);
                }
              });
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: loading ? (
              <>
                {loading ? <CircularProgress color='info' size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ) : inputValue ? (
              <InputAdornment position='end'>
                <IconButton onClick={clearAll}>
                  <GridClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      )}
    />
  );
}
